import { useEffect, useState } from "react";
import { Star, BadgeCheck, MessageSquareReply } from "lucide-react";
import { fetchHotelReviews, replyHotelReview } from "@/lib/api";
import { toast } from "sonner";

export const Stars = ({ value, size = 14 }) => (
  <span className="inline-flex" data-testid="stars">
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} className={n <= Math.round(value || 0) ? "fill-amber-400 text-amber-400" : "text-[var(--line)]"} />)}
  </span>
);

export const HotelReviewsSection = ({ hotelId, canReply = false }) => {
  const [data, setData] = useState(null);
  const [replying, setReplying] = useState(null);
  const [text, setText] = useState("");

  const load = () => fetchHotelReviews(hotelId).then(setData).catch(() => setData({ items: [], count: 0 }));
  useEffect(() => { load(); }, [hotelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendReply = async (id) => {
    try { await replyHotelReview(hotelId, id, text); toast.success("Reponse publiee"); setReplying(null); setText(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!data) return null;
  return (
    <section className="mt-6 glass-card p-7" data-testid="hotel-reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-bold text-lg inline-flex items-center gap-2"><BadgeCheck size={18} className="text-emerald-600" /> Avis verifies</h2>
        {data.count > 0 ? (
          <div className="inline-flex items-center gap-2 text-sm" data-testid="hotel-reviews-summary"><Stars value={data.average} /> <b>{data.average}</b>/5 · {data.count} avis</div>
        ) : <span className="text-sm text-[var(--ink-soft)]" data-testid="hotel-reviews-empty">Aucun avis pour l&apos;instant — seuls les clients ayant sejourne peuvent noter.</span>}
      </div>
      <ul className="space-y-4">
        {data.items.map((r) => (
          <li key={r.id} className="border-b border-[var(--line)]/60 pb-3" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-2 text-sm"><Stars value={r.rating} /> <b>{r.author}</b> <span className="text-xs text-[var(--ink-soft)]">· sejour du {r.stay_date}</span>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><BadgeCheck size={10} /> verifie</span></div>
            {r.comment && <p className="text-sm mt-1">{r.comment}</p>}
            {r.reply && <div className="mt-2 ml-4 text-sm rounded-xl bg-orange-50 p-3" data-testid={`review-reply-${r.id}`}><b>Reponse de l&apos;hotel :</b> {r.reply}</div>}
            {canReply && !r.reply && (replying === r.id ? (
              <div className="mt-2 flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre reponse publique" data-testid={`review-reply-input-${r.id}`} />
                <button onClick={() => sendReply(r.id)} className="btn-primary text-sm" data-testid={`review-reply-send-${r.id}`}>Publier</button>
              </div>
            ) : (
              <button onClick={() => setReplying(r.id)} className="mt-2 text-xs font-semibold text-orange-600 inline-flex items-center gap-1" data-testid={`review-reply-open-${r.id}`}><MessageSquareReply size={12} /> Repondre</button>
            ))}
          </li>
        ))}
      </ul>
    </section>
  );
};
